import {
  Alert,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Typography,
} from "@mui/material";
import * as React from "react";

import { SlackConnectionRecord } from "@/shared/domain/slack";
import {
  useSetSlackOutboundChannels,
  useSlackOutboundChannels,
  useSlackWorkspaceChannels,
} from "@/shared/domain/slack/hooks";
import { Button } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";

import styles from "./SlackChannelPicker.module.css";

interface SlackChannelPickerProps {
  orgId: string;
  connection: SlackConnectionRecord;
  /** Non-admins see the current allowlist but cannot change it. */
  readOnly: boolean;
}

function sameChannels(a: Set<string>, b: Set<string>): boolean {
  return a.size === b.size && [...a].every((channelId) => b.has(channelId));
}

/**
 * The outbound allowlist editor: which channels this org's agents may post into.
 *
 * Saving replaces the workspace's allowlist with the checked channels, and the backend refuses
 * ids it cannot see the bot in, so a channel the bot has left is dropped by any save — greyed
 * out and warned about rather than silently omitted. There is no compare-and-set: a refresh
 * catches a competing save, but nothing polls.
 *
 * Read-only viewers get the saved allowlist alone, because the workspace channel listing that
 * the editor is built from is admin-only.
 */
export function SlackChannelPicker({
  orgId,
  connection,
  readOnly,
}: SlackChannelPickerProps) {
  const workspace = React.useMemo(
    () => ({
      enterpriseId: connection.enterprise_id,
      teamId: connection.team_id,
    }),
    [connection.enterprise_id, connection.team_id],
  );

  // Listing a workspace's channels is ORG_ADMIN_ONLY, so a read-only viewer must not ask:
  // disabling the query leaves it unfired rather than 401ing over the allowlist they are
  // entitled to see.
  const {
    data: availableChannels,
    isLoading: channelsLoading,
    error: channelsError,
    refetch: refetchChannels,
  } = useSlackWorkspaceChannels(orgId, workspace, { enabled: !readOnly });
  const {
    data: allowlist,
    isLoading: allowlistLoading,
    error: allowlistError,
    refetch: refetchAllowlist,
  } = useSlackOutboundChannels(orgId);
  const setChannelsMutation = useSetSlackOutboundChannels(orgId);

  const savedEntries = React.useMemo(() => {
    return (allowlist ?? []).filter(
      (entry) =>
        entry.team_id === workspace.teamId &&
        entry.enterprise_id === workspace.enterpriseId,
    );
  }, [allowlist, workspace]);

  const savedSelection = React.useMemo(() => {
    return new Set(savedEntries.map((entry) => entry.channel_id));
  }, [savedEntries]);

  // `base` is the allowlist these edits were made against — the only handle on a competing save.
  const [draft, setDraft] = React.useState<{
    selected: Set<string>;
    base: Set<string>;
  } | null>(null);
  const selected = draft?.selected ?? savedSelection;

  // Approved channels missing from the listing the checkboxes are drawn from. Keyed on that
  // listing and not on `channelsError`, so a failed refetch — which leaves the last listing
  // rendered and editable — keeps warning about what a save would drop.
  const departedEntries = React.useMemo(() => {
    if (availableChannels === undefined) {
      return [];
    }
    const availableIds = new Set(
      availableChannels.map((channel) => channel.channel_id),
    );
    return savedEntries.filter((entry) => !availableIds.has(entry.channel_id));
  }, [availableChannels, savedEntries]);

  // Exactly what Save sends: a departed channel is checked but unsendable.
  const channelsToSave = React.useMemo(() => {
    return (availableChannels ?? [])
      .filter((channel) => selected.has(channel.channel_id))
      .map((channel) => channel.channel_id);
  }, [availableChannels, selected]);

  const toggleChannel = (channelId: string) => {
    const next = new Set(selected);
    if (next.has(channelId)) {
      next.delete(channelId);
    } else {
      next.add(channelId);
    }
    setDraft({ selected: next, base: draft?.base ?? savedSelection });
  };

  const handleSave = () => {
    setChannelsMutation.mutate(
      { workspace, channelIds: channelsToSave },
      { onSuccess: () => setDraft(null) },
    );
  };

  const handleRefresh = () => {
    void refetchChannels();
    void refetchAllowlist();
  };

  const isDirty =
    draft !== null && !sameChannels(draft.selected, savedSelection);
  const isStale = draft !== null && !sameChannels(draft.base, savedSelection);
  const willClearAll = isDirty && channelsToSave.length === 0;

  if (channelsLoading || allowlistLoading) {
    return (
      <div className={styles.loadingRow}>
        <CircularProgress size={16} />
        <Typography variant="body2">Loading channels…</Typography>
      </div>
    );
  }

  // Every box would read as unchecked, and saving that would clear an unseen allowlist.
  if (allowlistError !== null) {
    return (
      <div className={styles.picker}>
        <Typography variant="subtitle2">Approved channels</Typography>
        <Alert severity="error">
          Could not load the approved channels, so they cannot be edited:{" "}
          {userFacingErrorMessage(allowlistError)}
        </Alert>
      </div>
    );
  }

  const savedAllowlist =
    savedEntries.length === 0 ? (
      <Alert severity="info">
        No channels are approved, so Roboto agents cannot post to this
        workspace.
      </Alert>
    ) : (
      <div className={styles.channelList}>
        {savedEntries.map((entry) => (
          <Typography key={entry.channel_id} variant="body2">
            #{entry.channel_name}
          </Typography>
        ))}
      </div>
    );

  if (readOnly) {
    return (
      <div className={styles.picker}>
        <Typography variant="subtitle2">Approved channels</Typography>
        <Typography variant="body2" color="text.secondary">
          Roboto agents may post only to the channels listed here. Only
          organization admins can change the list.
        </Typography>
        {savedAllowlist}
      </div>
    );
  }

  // First load of the channel listing failed, so there is nothing to edit — but the allowlist
  // resolved fine, so show it read-only rather than hiding it behind the error. A failed
  // *refetch* keeps the last listing rendered and editable, and never lands here.
  if (channelsError !== null && availableChannels === undefined) {
    return (
      <div className={styles.picker}>
        <Typography variant="subtitle2">Approved channels</Typography>
        <Alert severity="error">
          Could not list this workspace&apos;s channels:{" "}
          {userFacingErrorMessage(channelsError)}
        </Alert>
        {savedAllowlist}
        <div className={styles.actions}>
          <Button variant="text" onClick={handleRefresh}>
            Refresh
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.picker}>
      <Typography variant="subtitle2">Approved channels</Typography>
      <Typography variant="body2" color="text.secondary">
        Roboto agents may post only to the channels checked here. To add a
        channel here, invite the Roboto app to it in Slack, then refresh.
      </Typography>

      {channelsError !== null && (
        <Alert severity="error">
          Could not list this workspace&apos;s channels:{" "}
          {userFacingErrorMessage(channelsError)}
        </Alert>
      )}

      {/* Removed-from-everywhere is not "not in any channel yet": the departed warning below
          already covers it, and stacking the invite copy on top would contradict it. */}
      {!channelsError &&
        (availableChannels ?? []).length === 0 &&
        departedEntries.length === 0 && (
          <Alert severity="info">
            The Roboto app is not in any channel yet. In Slack, run{" "}
            <code>/invite @Roboto</code> in the channel you want agents to post
            to, then refresh.
          </Alert>
        )}

      <div className={styles.channelList}>
        {(availableChannels ?? []).map((channel) => (
          <FormControlLabel
            key={channel.channel_id}
            control={
              <Checkbox
                size="small"
                checked={selected.has(channel.channel_id)}
                onChange={() => toggleChannel(channel.channel_id)}
                disabled={setChannelsMutation.isPending}
              />
            }
            label={`#${channel.name}${channel.is_private ? " (private)" : ""}`}
          />
        ))}
        {departedEntries.map((entry) => (
          <FormControlLabel
            key={entry.channel_id}
            control={<Checkbox size="small" checked disabled />}
            disabled
            label={`#${entry.channel_name} — Roboto is no longer in this channel`}
          />
        ))}
      </div>

      {departedEntries.length > 0 && (
        <Alert severity="warning">
          Roboto has been removed from {departedEntries.length} approved{" "}
          {departedEntries.length === 1 ? "channel" : "channels"}, shown greyed
          out above. Saving drops {departedEntries.length === 1 ? "it" : "them"}{" "}
          from the approved list. To keep{" "}
          {departedEntries.length === 1 ? "it" : "them"}, re-invite the Roboto
          app in Slack and refresh before saving.
        </Alert>
      )}

      {isStale && (
        <Alert severity="warning">
          Someone else changed the approved channels while you were editing.
          Discard your changes to load the current list, then re-apply yours —
          saving now would overwrite theirs.
        </Alert>
      )}

      {willClearAll && !isStale && (
        <Alert severity="warning">
          Saving with no channels approved revokes Roboto agents&apos; posting
          ability across the workspace until a channel is re-approved.
        </Alert>
      )}

      {setChannelsMutation.isError && (
        <Alert severity="error">
          Could not save the channel selection:{" "}
          {userFacingErrorMessage(setChannelsMutation.error)}
        </Alert>
      )}

      <div className={styles.actions}>
        <Button
          variant="text"
          onClick={handleRefresh}
          disabled={setChannelsMutation.isPending}
        >
          Refresh
        </Button>
        {/* isStale alone can hold even when the draft matches the refetched allowlist, and the
            stale warning names Discard as the exit — so it must render there too. */}
        {(isDirty || isStale) && (
          <Button
            variant="text"
            onClick={() => setDraft(null)}
            disabled={setChannelsMutation.isPending}
          >
            Discard changes
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isDirty || isStale}
          loading={setChannelsMutation.isPending}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
